/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    generateImageUrlForOmniChanel,
    IAction,
    IActionContext,
    IActionInput,
    IRequestContext,
    versionGte
} from '@msdyn365-commerce/core-internal';
// @ts-ignore
import { MediaLocation, MediaType } from '@msdyn365-commerce/retail-proxy';
import { createObservableDataAction } from '../create-data-action.vnext';

export enum MediaLocationType {
    All,
    Image,
    Video,
    File
}

/**
 * The action input class for the getAdditionalMediaLocationsForSelectedVariant data action.
 */
export class AdditionalMediaLocationsInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId: number;

    // defaulting to image for backwards compatibility, old version filters Image by default.
    public typeFilter: MediaLocationType;

    private readonly locale?: string;

    constructor(context: IRequestContext, productId: number, channelId: number, catalogId?: number, typeFilter = MediaLocationType.All) {
        this.productId = productId;
        this.channelId = channelId;
        this.catalogId = catalogId || 0;
        this.typeFilter = typeFilter;
        this.locale = context.locale;
    }

    public getCacheKey = () => `${this.productId}-${this.channelId}-${this.catalogId}-${this.locale}}-${this.typeFilter}`;

    public getCacheObjectType = () => 'ProductAdditionalMediaLocations';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * The action method for the getAdditionalMediaLocationsForSelectedVariant data action.
 * @param input
 * @param ctx
 */
export async function getAdditionalMediaLocationsForProductAction(
    input: AdditionalMediaLocationsInput,
    ctx: IActionContext
): Promise<MediaLocation[]> {
    const rsVersion =
        !process.env.MSDyn365Commerce_RSVERSION || process.env.MSDyn365Commerce_RSVERSION === '--'
            ? '0.0'
            : process.env.MSDyn365Commerce_RSVERSION;
    const OmniChannelAdditionalMediaFeatureVersion = versionGte(rsVersion, '9.46');
    if (!OmniChannelAdditionalMediaFeatureVersion) {
        return [];
    }
    // the types and productActions are not available in all versions of retail-proxy, so they are imported by direct reference after comparing the version to the minimum required(9.46)
    // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires -- Need for Omni feature.,
    const types = require('@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g');
    // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires -- Need for Omni feature.,
    const productActions = require('@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g');

    // TODO: once the dynamicProvider api for fetching the media locations from the azureCms is implemented
    // we should uncomment these code so we can preview the product in edit mode without publish the product.
    // if (ctx.requestContext.params && ctx.requestContext.params.isEditor) {
    //     return getOmniChannelMediaInfoAsync(ctx.requestContext, input.productId);
    // }

    let productMediaLocations = [];
    try {
        productMediaLocations = await productActions.searchMediaLocationsAsync(
            {
                callerContext: ctx,
                queryResultSettings: {
                    Paging: { Top: Number.parseInt(ctx.requestContext?.app?.platform?.defaultPageSizeForAPI ?? 100, 10) }
                }
            },
            {
                ProductId: input.productId,
                ChannelId: input.channelId,
                CatalogId: input.catalogId,
                MediaUsageTypeValue: 2
            }
        );
    } catch (error) {
        ctx.telemetry.error(`Error running searchMediaLocationsAsync action: ${error}`);
        return [];
    }

    // Need to add a second enum for MediaTypeValue because the RetailServer API returns a different enum for the MediaTypeValue property,
    // where default is "None" instead of "All"
    let filteredMedia: MediaLocation[] = [];
    switch (input.typeFilter) {
        case MediaLocationType.Image:
            // @ts-ignore
            filteredMedia = productMediaLocations.filter(media => media.MediaTypeValue === types.MediaType.Image);
            break;
        case MediaLocationType.Video:
            // @ts-ignore
            filteredMedia = productMediaLocations.filter(media => media.MediaTypeValue === types.MediaType.Video);
            break;
        case MediaLocationType.File:
            // @ts-ignore
            filteredMedia = productMediaLocations.filter(media => media.MediaTypeValue === types.MediaType.File);
            break;
        default:
            filteredMedia = productMediaLocations;
    }
    if (filteredMedia.length === 0) {
        return [];
    }

    // sorting the images by display order settings.
    filteredMedia = filteredMedia.sort((a, b) => {
        // @ts-ignore
        if (typeof a.DisplayOrder === 'undefined' && typeof b.DisplayOrder === 'undefined') {
            return 0;
            // @ts-ignore
        } else if (typeof a.DisplayOrder === 'undefined') {
            return 1;
            // @ts-ignore
        } else if (typeof b.DisplayOrder === 'undefined') {
            return -1;
        } else {
            // @ts-ignore
            return a.DisplayOrder - b.DisplayOrder;
        }
    });
    const productMedia = filteredMedia.map((mediaLocation: MediaLocation) => {
        mediaLocation.Uri = generateImageUrlForOmniChanel(ctx.requestContext.apiSettings, mediaLocation.Uri);
        mediaLocation.AltText = mediaLocation.AltText;
        return mediaLocation;
    });
    return productMedia;
}

/**
 * The getAdditionalMediaLocationsForSelectedVariantActionDataAction data action
 * Gets the currently selected variant for the page via the getSelectedVariant data action,
 * and then gets the media location information for the variant via the MediaLocations RetailServer API.
 */
export const getAdditionalMediaLocationsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce/get-additional-mediaLocations',
    action: <IAction<MediaLocation[] | null>>getAdditionalMediaLocationsForProductAction
});

export default getAdditionalMediaLocationsForProductAction;
